import { useCallback, useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { assetUrl } from "../lib/api";

/**
 * Full-screen photo lightbox — v2 vertical layout.
 *
 * Structure (top → bottom):
 *   [ close X (absolute, top-right of viewport) ]
 *   [ image  — max-h-[60vh] sm:max-h-[68vh], rounded-2xl, soft top-glow ]
 *   [ 20 px gap ]
 *   [ counter pill — centered ]
 *   [ 16 px gap ]
 *   [ filmstrip — centered, contained to ~min(80vw, 720px), horizontal scroll ]
 *
 * Arrows are absolutely positioned to the left/right edges of the viewport,
 * vertically centered. Close is absolute top-right. Everything else is in a
 * plain flex column that gives real vertical rhythm — no elements overlap
 * the image.
 */
export default function PhotoLightbox({
  photos = [],
  initialIndex = 0,
  isOpen = false,
  onClose,
  altPrefix = "Photo",
}) {
  const [index, setIndex] = useState(initialIndex);
  const [direction, setDirection] = useState(0);
  const touchStartX = useRef(null);
  const filmstripRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setIndex(initialIndex);
      setDirection(0);
    }
  }, [isOpen, initialIndex]);

  // Body scroll lock — preserves scroll position on close.
  useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY;
    const b = document.body.style;
    const prev = {
      overflow: b.overflow,
      position: b.position,
      top: b.top,
      width: b.width,
    };
    b.overflow = "hidden";
    b.position = "fixed";
    b.top = `-${scrollY}px`;
    b.width = "100%";
    return () => {
      b.overflow = prev.overflow;
      b.position = prev.position;
      b.top = prev.top;
      b.width = prev.width;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  const total = photos.length;
  const hasPrev = index > 0;
  const hasNext = index < total - 1;

  const prev = useCallback(() => {
    setIndex((i) => {
      if (i <= 0) return i;
      setDirection(-1);
      return i - 1;
    });
  }, []);
  const next = useCallback(() => {
    setIndex((i) => {
      if (i >= total - 1) return i;
      setDirection(1);
      return i + 1;
    });
  }, [total]);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, prev, next, onClose]);

  // Preload next photo so nav feels instant.
  useEffect(() => {
    if (!isOpen || !photos[index + 1]) return;
    const img = new Image();
    img.src = assetUrl(photos[index + 1]);
  }, [isOpen, index, photos]);

  // Scroll active filmstrip thumb to horizontal center on index change.
  useEffect(() => {
    if (!isOpen || !filmstripRef.current) return;
    const el = filmstripRef.current.querySelector(
      `[data-thumb-index='${index}']`
    );
    if (el && el.scrollIntoView) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [isOpen, index]);

  function onTouchStart(e) {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  }
  function onTouchEnd(e) {
    if (touchStartX.current == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 50) return;
    if (dx > 0) prev();
    else next();
  }

  if (!isOpen || total === 0) return null;

  const imgAnim =
    direction === -1
      ? "animate-in fade-in slide-in-from-left-4 duration-300 ease-out"
      : direction === 1
      ? "animate-in fade-in slide-in-from-right-4 duration-300 ease-out"
      : "animate-in fade-in zoom-in-[0.94] duration-300 ease-out";

  return (
    <div
      className="fixed inset-0 z-50 bg-black backdrop-blur-2xl animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      onClick={onClose}
      data-testid="photo-lightbox"
    >
      {/* Close (viewport top-right) */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose?.();
        }}
        className="absolute top-6 right-6 z-10 w-11 h-11 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-white hover:bg-white/20 transition-all duration-200 inline-flex items-center justify-center animate-in fade-in duration-300 delay-100 fill-mode-both"
        aria-label="Close photo viewer"
        data-testid="lightbox-close"
      >
        <X size={20} strokeWidth={2} />
      </button>

      {/* Prev arrow (viewport left, desktop only) */}
      {hasPrev && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            prev();
          }}
          className="hidden sm:inline-flex absolute top-1/2 -translate-y-1/2 left-6 z-10 w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-white hover:bg-white/20 transition-all duration-200 items-center justify-center animate-in fade-in duration-300 delay-100 fill-mode-both"
          aria-label="Previous photo"
          data-testid="lightbox-prev"
        >
          <ChevronLeft size={22} strokeWidth={2} />
        </button>
      )}

      {/* Next arrow (viewport right, desktop only) */}
      {hasNext && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            next();
          }}
          className="hidden sm:inline-flex absolute top-1/2 -translate-y-1/2 right-6 z-10 w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-white hover:bg-white/20 transition-all duration-200 items-center justify-center animate-in fade-in duration-300 delay-100 fill-mode-both"
          aria-label="Next photo"
          data-testid="lightbox-next"
        >
          <ChevronRight size={22} strokeWidth={2} />
        </button>
      )}

      {/* Vertical content column — image on top, then counter, then filmstrip. */}
      <div
        className="min-h-screen flex flex-col items-center justify-center py-6 gap-5 px-4 sm:px-20"
        onClick={onClose}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <img
          key={index}
          src={assetUrl(photos[index])}
          alt={`${altPrefix} ${index + 1} of ${total}`}
          className={`max-h-[60vh] max-w-[92vw] sm:max-h-[68vh] sm:max-w-[82vw] object-contain rounded-2xl shadow-[0_20px_80px_-20px_rgba(255,255,255,0.15)] select-none ${imgAnim}`}
          draggable={false}
          onClick={(e) => e.stopPropagation()}
          data-testid={`lightbox-image-${index}`}
        />

        {/* Counter pill — below the image with breathing room */}
        <div
          className="inline-flex items-center bg-white/10 backdrop-blur-md text-white text-sm font-medium px-3 py-1.5 rounded-full border border-white/15 tabular-nums animate-in fade-in duration-300 delay-100 fill-mode-both"
          onClick={(e) => e.stopPropagation()}
          data-testid="lightbox-counter"
        >
          {index + 1} / {total}
        </div>

        {/* Filmstrip — contained width, horizontally scrollable */}
        {total > 1 && (
          <div
            ref={filmstripRef}
            className="hbe-scroll-hide flex gap-2 sm:gap-2 overflow-x-auto max-w-[min(92vw,720px)] px-2 py-1 scroll-smooth animate-in fade-in duration-300 delay-150 fill-mode-both"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-filmstrip"
          >
            {photos.map((src, i) => (
              <button
                type="button"
                key={src}
                onClick={() => {
                  setDirection(i > index ? 1 : i < index ? -1 : 0);
                  setIndex(i);
                }}
                className={`shrink-0 w-12 h-12 sm:w-16 sm:h-16 overflow-hidden rounded-lg transition-all duration-200 ${
                  i === index
                    ? "opacity-100 ring-2 ring-white ring-offset-2 ring-offset-black scale-105"
                    : "opacity-50 hover:opacity-100"
                }`}
                aria-label={`Jump to photo ${i + 1}`}
                data-thumb-index={i}
                data-testid={`lightbox-thumb-${i}`}
              >
                <img
                  src={assetUrl(src)}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
